import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { SelectOption } from '@app/app/shared/styleguide/components/selects/sg-select/sg-select.component';
import { TranslocoService } from '@ngneat/transloco';
import { Store } from '@ngxs/store';
import { ROLES_DATA_SERVICE, RolesDataService } from 'src/app/page-modules/admin/services/roles/roles-data.service';
import { LanguageCodeHelper } from 'src/app/shared/helpers/language-code-helper';
import { OrganizationRoles } from 'src/app/shared/models';
import { UserAuthState } from 'src/app/user-auth/store/user-auth.state';
import {
  AndRequest,
  BooleanFilter,
  BooleanQueryRequest,
  BooleanQueryType,
  NotRequest,
  OrRequest,
  RolesRequest,
} from '../../../../models/admin/boolean-filters.model';

const DEFAULT_ROLE_OPERATOR = 'OR';

@Component({
  selector: 'ptl-v3-role-filter',
  templateUrl: './v3-role-filter.component.html',
  styleUrls: ['./v3-role-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class V3RoleFilterComponent implements OnInit {
  @Input() selectedFilters: BooleanFilter[];
  @Output() filterDeleted = new EventEmitter<void>();
  @Output() filterSaved = new EventEmitter<BooleanFilter>();

  logicalOperator: 'OR' | 'AND' = DEFAULT_ROLE_OPERATOR;
  roleIs = true;
  roles: { role: OrganizationRoles; value: boolean; label: string }[] = [];
  roleIsOptions: SelectOption<boolean>[] = [];
  operatorOptions: SelectOption<'OR' | 'AND'>[] = [];

  constructor(
    @Inject(ROLES_DATA_SERVICE) private rolesDataService: RolesDataService,
    private translocoService: TranslocoService,
    private store: Store,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.roleIsOptions = [
      { value: true, label: this.translocoService.translate('translations.filter.is') },
      { value: false, label: this.translocoService.translate('translations.filter.isNot') },
    ];
    this.operatorOptions = [
      { value: 'AND', label: this.translocoService.translate('translations.logicalOperators.and') },
      { value: 'OR', label: this.translocoService.translate('translations.logicalOperators.or') },
    ];
    this.initRoles();
  }

  setRoleIs() {
    this.roleIs = true;
  }

  setRoleIsNot() {
    this.roleIs = false;
    this.logicalOperator = DEFAULT_ROLE_OPERATOR;
  }

  onRoleIsSelection(roleIs: boolean): void {
    if (roleIs) {
      this.setRoleIs();
    } else {
      this.setRoleIsNot();
    }
  }

  deleteFilter() {
    this.filterDeleted.emit();
  }

  applyFilter() {
    const request = this.getRequest(this.roles.filter((role) => role.value === true).map((role) => role.role._id));
    const message = this.getMessage(
      this.roles.filter((role) => role.value === true).map((role) => LanguageCodeHelper.getDataByLanguageCode(role.role.name).value),
    );
    this.filterSaved.emit({ request: request, message: message, type: 'ROLE' });
  }

  private initRoles() {
    const organizationUid = this.store.selectSnapshot(UserAuthState.organizationDetails)._id;
    this.rolesDataService.loadOrganizationRoles(organizationUid).subscribe(({ isSuccess, value }) => {
      if (isSuccess) {
        value.forEach((role) => {
          this.roles.push({ role: role, value: false, label: LanguageCodeHelper.getDataByLanguageCode(role.name).value });
        });
        this.roles.sort((a, b) => a.label.localeCompare(b.label));
        this.getSettingsFromFilters();
        this.cdr.detectChanges();
      }
    });
  }

  private getRequest(roleIds: string[]): BooleanQueryRequest {
    if (this.logicalOperator !== DEFAULT_ROLE_OPERATOR) {
      const andRequest = {
        type: BooleanQueryType.AND,
        args: roleIds.map((id) => ({
          type: BooleanQueryType.ROLES,
          roles: [id],
        })),
      };
      return this.roleIs ? andRequest : ({ type: BooleanQueryType.NOT, arg: andRequest } as BooleanQueryRequest);
    }

    const request: RolesRequest = { type: BooleanQueryType.ROLES, roles: roleIds };
    if (this.roleIs) {
      return request as BooleanQueryRequest;
    } else {
      return { type: BooleanQueryType.NOT, arg: request } as BooleanQueryRequest;
    }
  }

  private getMessage(labels: string[]): string {
    if (this.roleIs) {
      const operator =
        this.logicalOperator === 'OR'
          ? this.translocoService.translate('translations.reviews.filter.or')
          : this.translocoService.translate('translations.filter.and');

      return (
        this.translocoService.translate('translations.filter.role.name') +
        ' ' +
        this.translocoService.translate('translations.filter.is') +
        ' ' +
        labels.join(' ' + operator + ' ')
      );
    } else {
      return (
        this.translocoService.translate('translations.filter.role.name') +
        ' ' +
        this.translocoService.translate('translations.filter.isNot') +
        ' ' +
        labels.join(' ' + this.translocoService.translate('translations.filter.andNot') + ' ')
      );
    }
  }

  private getSettingsFromFilters() {
    if (this.selectedFilters) {
      const statusFilter = this.selectedFilters.find((f) => f.type === 'ROLE');
      if (statusFilter) {
        const booleanRequest = statusFilter.request as BooleanQueryRequest;
        let rolesIds: string[] = [];

        if (booleanRequest.type === BooleanQueryType.AND) {
          this.logicalOperator = 'AND';
          rolesIds = (booleanRequest as AndRequest).args
            .filter((arg) => arg.type === BooleanQueryType.ROLES)
            .flatMap((arg) => (arg as RolesRequest).roles);
        } else if (booleanRequest.type === BooleanQueryType.OR) {
          this.logicalOperator = 'OR';
          rolesIds = (booleanRequest as OrRequest).args
            .filter((arg) => arg.type === BooleanQueryType.ROLES)
            .flatMap((arg) => (arg as RolesRequest).roles);
        } else if (booleanRequest.type === BooleanQueryType.NOT) {
          this.roleIs = false;
          const arg = (booleanRequest as NotRequest).arg;
          if (arg.type === BooleanQueryType.AND || arg.type === BooleanQueryType.OR) {
            this.logicalOperator = arg.type === BooleanQueryType.AND ? 'AND' : 'OR';
            rolesIds = (arg as AndRequest).args
              .filter((inner) => inner.type === BooleanQueryType.ROLES)
              .flatMap((inner) => (inner as RolesRequest).roles);
          } else {
            rolesIds = (arg as RolesRequest).roles;
          }
        } else {
          this.roleIs = true;
          rolesIds = (booleanRequest as RolesRequest).roles;
        }
        this.setRoleSelected(rolesIds);
      }
    }
  }

  setRoleSelected(roleIds: string[]) {
    this.roles = this.roles.map((r) => {
      if (roleIds.includes(r.role._id)) {
        r.value = true;
      }
      return r;
    });
  }

  changeRole(role: OrganizationRoles) {
    this.roles = this.roles.map((r) => {
      if (role === r.role) {
        r.value = !r.value;
      }
      return r;
    });
  }

  onLogicalOperatorSelection(operator: 'OR' | 'AND'): void {
    this.logicalOperator = operator;
    this.roleIs = true;
  }
}
